import React from "react";
import type { ImportRecipientsResult } from "@kkp/api-client";
import { Download, Upload, UserCheck, UserX } from "lucide-react";
import { PageContainer } from "@kkp/ui";
import { Button } from "@kkp/ui";
import { Card } from "@kkp/ui";
import { Input } from "../../ui/Input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { useQuery } from "@tanstack/react-query";
import { formatBankDetails } from "../../lib/mock-data";
import {
  downloadRecipientTemplate,
  importRecipients,
  listRecipients,
  updateRecipientStatus,
} from "../../api/recipients";
import { formatCurrency, formatNumber } from "@kkp/app-kit";
import { perClientSeqNo } from "../../lib/seq";
import { apiErrorMessage } from "../../lib/apiErrorMessage";
import { toast } from "@kkp/ui";

const EMPTY_VALIDATION: ImportRecipientsResult = {
  alreadyExistCount: "0",
  newUserCount: "0",
  badAccounts: [],
};

const EMPTY_RECIPIENTS: Awaited<ReturnType<typeof listRecipients>> = [];

export default function RecipientsPage() {
  const { data, refetch } = useQuery({
    queryKey: ["recipients"],
    queryFn: listRecipients,
    staleTime: 0,
  });
  const recipients = data ?? EMPTY_RECIPIENTS;
  // Per-client Seq ID (1..N by creation), independent of the global seq_id.
  const recipientSeqNo = perClientSeqNo(recipients.map((r) => r.seqId));
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [bankFilter, setBankFilter] = React.useState("all");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogStep, setDialogStep] = React.useState<
    "idle" | "validating" | "result"
  >("idle");
  const [validation, setValidation] = React.useState(EMPTY_VALIDATION);
  const [fileInputKey, setFileInputKey] = React.useState(0);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const timerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const activeCount = recipients.filter((r) => r.status === "Active").length;
  const inactiveCount = recipients.length - activeCount;

  const bankOptions = React.useMemo(() => {
    const banks = Array.from(new Set(recipients.map((r) => r.bankName)));
    return banks.sort();
  }, [recipients]);

  const filteredRecipients = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return recipients.filter((r) => {
      const matchesSearch =
        !query ||
        r.fullName.toLowerCase().includes(query) ||
        r.employeeId.toLowerCase().includes(query) ||
        r.ktpId.toLowerCase().includes(query) ||
        r.npwpId.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "all" || r.status.toLowerCase() === statusFilter;

      const matchesBank =
        bankFilter === "all" || r.bankName.toLowerCase() === bankFilter;

      return matchesSearch && matchesStatus && matchesBank;
    });
  }, [recipients, search, statusFilter, bankFilter]);

  const downloadCsv = (filename: string, rows: string[][]) => {
    const csv = rows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    downloadRecipientTemplate()
      .then((csv) => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "recipients-template.csv";
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.danger("Failed to download template.", {
          title: "Recipients",
        });
      });
  };

  const handleUploadClick = () => {
    resetFileInput();
    fileInputRef.current?.click();
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    setDialogOpen(true);
    setDialogStep("validating");

    importRecipients(file)
      .then((result) => {
        setValidation(result);
        void refetch();
      })
      .catch((err: unknown) => {
        setValidation(EMPTY_VALIDATION);
        toast.danger(apiErrorMessage(err, "Failed to import recipients."), {
          title: "Recipients",
        });
      })
      .finally(() => {
        if (timerRef.current) {
          window.clearTimeout(timerRef.current);
        }
        timerRef.current = window.setTimeout(() => {
          setDialogStep("result");
        }, 700);
        input.value = "";
        resetFileInput();
        setFileInputKey((key) => key + 1);
      });
  };

  const handleToggleStatus = (seqId: string) => {
    const current = recipients.find((recipient) => recipient.seqId === seqId);
    if (!current) return;
    updateRecipientStatus(
      seqId,
      current.status === "Active" ? "Inactive" : "Active",
    )
      .then(() => refetch())
      .catch(() => {
        toast.danger("Failed to update recipient status.", {
          title: "Recipients",
        });
      });
  };

  const handleDownloadBadAccounts = () => {
    const rows = [
      ["Employee ID", "Full Name", "Bank Details"],
      ...validation.badAccounts.map((item) => [
        item.employeeId,
        item.fullName,
        item.bankDetails,
      ]),
    ];
    downloadCsv("bad-bank-accounts.csv", rows);
  };

  return (
    <PageContainer
      title="Recipients"
      description="Manage active recipients and bulk recipient onboarding."
      width="full"
      actions={
        <div className="grid gap-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4" />
              Download CSV Template
            </Button>
            <Button variant="primary" onClick={handleUploadClick}>
              <Upload className="h-4 w-4" />
              Upload CSV
            </Button>
          </div>
          <p className="text-xs text-[color:var(--kkp-text-muted)]">
            E-wallet: put OVO / GOPAY / DANA / SHOPEEPAY in Bank Code and the
            e-wallet phone number in Bank Account.
          </p>
        </div>
      }
    >
      <input
        key={`recipients-upload-${fileInputKey}`}
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileSelected}
      />

      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card>
            <div className="text-xs text-[color:var(--kkp-text-muted)]">
              Active recipients
            </div>
            <div className="mt-2 text-xl font-semibold text-white">
              {formatNumber(activeCount)}
            </div>
          </Card>
          <Card>
            <div className="text-xs text-[color:var(--kkp-text-muted)]">
              Inactive recipients
            </div>
            <div className="mt-2 text-xl font-semibold text-white">
              {formatNumber(inactiveCount)}
            </div>
          </Card>
        </div>

        <Card className="grid gap-3">
          <div className="text-sm font-semibold text-white">Filters</div>
          <div className="grid gap-3 lg:grid-cols-[1.5fr_0.75fr_0.75fr]">
            <Input
              placeholder="Search by name, employee ID, KTP/Passport, or NPWP"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:var(--kkp-surface-2)] px-3 py-2.5 text-sm text-white"
            >
              <option
                value="all"
                className="bg-[color:var(--kkp-surface)] text-white"
              >
                All status
              </option>
              <option
                value="active"
                className="bg-[color:var(--kkp-surface)] text-white"
              >
                Active
              </option>
              <option
                value="inactive"
                className="bg-[color:var(--kkp-surface)] text-white"
              >
                Inactive
              </option>
            </select>
            <select
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
              className="w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:var(--kkp-surface-2)] px-3 py-2.5 text-sm text-white"
            >
              <option
                value="all"
                className="bg-[color:var(--kkp-surface)] text-white"
              >
                All banks
              </option>
              {bankOptions.map((bank) => (
                <option
                  key={bank}
                  value={bank.toLowerCase()}
                  className="bg-[color:var(--kkp-surface)] text-white"
                >
                  {bank}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="flex flex-col gap-1 border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3">
            <div className="text-sm font-semibold text-white">
              Recipient list
            </div>
            <div className="text-xs text-[color:var(--kkp-text-muted)]">
              Showing {filteredRecipients.length} of {recipients.length} total
              recipients.
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[color:var(--kkp-surface-2)]">
                <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                  <th className="px-4 py-3">Seq ID</th>
                  <th className="px-4 py-3">Full Name</th>
                  <th className="px-4 py-3">Employee ID</th>
                  <th className="px-4 py-3">KTP/PASSPORT ID</th>
                  <th className="px-4 py-3">NPWP ID</th>
                  <th className="px-4 py-3">Bank Details</th>
                  <th className="px-4 py-3">Mobile Phone</th>
                  <th className="px-4 py-3">Total Received Count</th>
                  <th className="px-4 py-3">Total Received Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipients.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      No recipients match the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredRecipients.map((recipient) => (
                    <tr
                      key={recipient.seqId}
                      className="border-t border-[color:rgba(255,255,255,0.08)]"
                    >
                      <td className="px-4 py-3 font-semibold text-white">
                        {recipientSeqNo.get(recipient.seqId) ?? recipient.seqId}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {recipient.fullName}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {recipient.employeeId}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {recipient.ktpId}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {recipient.npwpId}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {formatBankDetails(
                          recipient.bankName,
                          recipient.bankCode,
                          recipient.bankAccount,
                        )}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {recipient.phone ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {formatNumber(recipient.totalReceivedCount)}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {formatCurrency(recipient.totalReceivedAmount)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            recipient.status === "Active"
                              ? "rounded-full bg-emerald-500/15 px-2 py-1 text-xs font-semibold text-emerald-200"
                              : "rounded-full bg-amber-500/15 px-2 py-1 text-xs font-semibold text-amber-200"
                          }
                        >
                          {recipient.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant={
                            recipient.status === "Active"
                              ? "ghost"
                              : "secondary"
                          }
                          onClick={() => handleToggleStatus(recipient.seqId)}
                        >
                          {recipient.status === "Active" ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                          {recipient.status === "Active"
                            ? "Deactivate"
                            : "Activate"}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setDialogStep("idle");
          }
        }}
      >
        <DialogContent wide>
          {dialogStep === "validating" ? (
            <>
              <DialogHeader>
                <DialogTitle>Validating CSV</DialogTitle>
                <DialogDescription>
                  Please wait while we validate the uploaded recipients file.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-3">
                <div className="h-2 w-full overflow-hidden rounded-full bg-[color:rgba(255,255,255,0.08)]">
                  <div className="h-full w-2/3 animate-pulse rounded-full bg-[color:var(--kkp-magenta)]" />
                </div>
                <div className="text-xs text-[color:var(--kkp-text-muted)]">
                  Validating file format, recipient uniqueness, and bank account
                  details.
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Validation Result</DialogTitle>
                <DialogDescription>
                  Positive validation result. You can proceed to bulk add
                  recipients.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Card variant="compact">
                  <div className="text-xs text-[color:var(--kkp-text-muted)]">
                    Already exist count
                  </div>
                  <div className="mt-2 text-lg font-semibold text-white">
                    {formatNumber(validation.alreadyExistCount)}
                  </div>
                </Card>
                <Card variant="compact">
                  <div className="text-xs text-[color:var(--kkp-text-muted)]">
                    New user count
                  </div>
                  <div className="mt-2 text-lg font-semibold text-white">
                    {formatNumber(validation.newUserCount)}
                  </div>
                </Card>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">
                    Bad bank accounts
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleDownloadBadAccounts}
                  >
                    <Download className="h-4 w-4" />
                    Download CSV
                  </Button>
                </div>
                <div className="mt-3 overflow-auto rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)]">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-[color:var(--kkp-surface-2)]">
                      <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                        <th className="px-4 py-2.5">Employee ID</th>
                        <th className="px-4 py-2.5">Full Name</th>
                        <th className="px-4 py-2.5">Bank Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.badAccounts.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-5 text-center text-sm text-[color:var(--kkp-text-muted)]"
                          >
                            No bad bank accounts found.
                          </td>
                        </tr>
                      ) : (
                        validation.badAccounts.map((item) => (
                          <tr
                            key={`${item.employeeId}-${item.bankDetails}`}
                            className="border-t border-[color:rgba(255,255,255,0.08)]"
                          >
                            <td className="px-4 py-2.5 text-white">
                              {item.employeeId}
                            </td>
                            <td className="px-4 py-2.5 text-white">
                              {item.fullName}
                            </td>
                            <td className="px-4 py-2.5 text-[color:var(--kkp-text-muted)]">
                              {item.bankDetails}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialogOpen(false);
                    setDialogStep("idle");
                  }}
                >
                  OK
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
